import { useFormContext } from 'react-hook-form';
import { getFormattedDate } from '../../../../../../../../../../utils/getFormattedDate';
import { useDelegateAdHocFormTransformedInputFields } from '../../../../../../../../hooks/confirmPurchase/delegate/useDelegateAdHocFormTransformedInputFields';
import { PricingFormInputType } from '../../../../../../../../types/enums';
import { pricingAcceptedFileTypes } from '../../../../../../../../types/constants';
import { PricingFormInput } from '../../../../../../../../store/features/delegate/types';
import TextField from '../../../../../../../../shared/formElements/TextField';
import PhoneField from '../../../../../../../../shared/formElements/PhoneField';
import DateField from '../../../../../../../../shared/formElements/DateField';
import FileField from '../../../../../../../../shared/formElements/FileField';
import { HTMLtoJSX } from '../../../../../../../../../../helpers';

export interface AdHocFieldProps {
  formInput: PricingFormInput;
}

export default function AdHocField(props: AdHocFieldProps) {
  const { formInput } = props;
  const { type, subLabel, limit, size, placeholder, pattern } = formInput;
  const { setValue, trigger } = useFormContext();
  const { fieldName, fieldLabel, fieldRequired } =
    useDelegateAdHocFormTransformedInputFields(formInput);

  switch (type) {
    case PricingFormInputType.TEXT: {
      return (
        <TextField
          name={fieldName}
          label={fieldLabel}
          placeholder={placeholder}
          helperText={HTMLtoJSX(subLabel)}
          rules={{
            required: fieldRequired,
            inFrontAllowedChars: true,
            format: pattern,
          }}
        />
      );
    }

    case PricingFormInputType.EMAIL: {
      return (
        <TextField
          type="email"
          name={fieldName}
          label={fieldLabel}
          helperText={HTMLtoJSX(subLabel)}
          rules={{ required: fieldRequired }}
        />
      );
    }

    case PricingFormInputType.TELEPHONE: {
      return (
        <PhoneField
          name={fieldName}
          label={fieldLabel}
          helperText={HTMLtoJSX(subLabel)}
          rules={{ required: fieldRequired }}
        />
      );
    }

    case PricingFormInputType.TEXTAREA: {
      return (
        <TextField
          name={fieldName}
          label={fieldLabel}
          helperText={HTMLtoJSX(subLabel)}
          rules={{ required: fieldRequired, inFrontAllowedChars: true }}
          multiline
          minRows={4}
        />
      );
    }

    case PricingFormInputType.DATE: {
      return (
        <DateField
          name={fieldName}
          label={fieldLabel}
          helperText={HTMLtoJSX(subLabel)}
          onChange={(value) => {
            if (value) {
              setValue(fieldName, getFormattedDate(value, 'yyyy-MM-dd'));
              trigger(fieldName);
            }
          }}
          rules={{ required: fieldRequired }}
        />
      );
    }

    case PricingFormInputType.DATE_MM: {
      return (
        <DateField
          name={fieldName}
          label={fieldLabel}
          helperText={HTMLtoJSX(subLabel)}
          onChange={(value) => {
            if (value) {
              setValue(fieldName, getFormattedDate(value, 'yyyy-MM'));
              trigger(fieldName);
            }
          }}
          views={['year', 'month']}
          rules={{ required: fieldRequired }}
        />
      );
    }

    case PricingFormInputType.DATE_YY: {
      return (
        <DateField
          name={fieldName}
          label={fieldLabel}
          helperText={HTMLtoJSX(subLabel)}
          onChange={(value) => {
            if (value) {
              setValue(fieldName, getFormattedDate(value, 'yyyy'));
              trigger(fieldName);
            }
          }}
          views={['year']}
          rules={{ required: fieldRequired }}
        />
      );
    }

    case PricingFormInputType.FILE:
    case PricingFormInputType.FILE_GROUP: {
      return (
        <FileField
          name={fieldName}
          label={fieldLabel}
          helperText={HTMLtoJSX(subLabel)}
          fileTypes={pricingAcceptedFileTypes}
          limit={type === PricingFormInputType.FILE ? 1 : limit}
          rules={{
            maxFileSize: {
              value: size,
            },
            atLeastOneFile: fieldRequired,
          }}
        />
      );
    }

    default: {
      return null;
    }
  }
}
